import {isEmpty, omit} from "lodash";
import {createReducer} from "redux-act";
import {
	changeRequestState,
	clearGameError,
	editUserAnswers,
	getIsFriendlyCountrySuccess,
	IRequestUserAnswerSuccessResponse,
	ISaveUserAnswersSuccess,
	requestUserAnswers,
	requestUserAnswersFailed,
	requestUserAnswersSuccess,
	saveUserAnswers,
	saveUserAnswersFailed,
	saveUserAnswersSuccess,
	setActiveStep,
	setAnchorId,
	setAnswers,
	updateQuestionAnswer,
} from "modules/actions";
import {ActiveStep, IsFriendly, RequestState} from "modules/enums";
import {IFormError} from "modules/types";

export interface IAnswer {
	questionId: number;
	optionId: number;
}

interface IGameReducer {
	step: ActiveStep;
	errors: IFormError;
	requestState: RequestState;
	saveRequestState: RequestState;
	answers: IAnswer[];
	savedOptions: number[];
	isFriendlyCountry: IsFriendly;
	anchorId: number | null;
}

const initialState: IGameReducer = {
	step: ActiveStep.PREDICTION,
	errors: {},
	requestState: RequestState.IDLE,
	saveRequestState: RequestState.IDLE,
	answers: [],
	savedOptions: [],
	isFriendlyCountry: IsFriendly.NO,
	anchorId: null,
};

const onSetActiveStep = (state: IGameReducer, payload: ActiveStep): IGameReducer => ({
	...state,
	step: payload,
});

const onClearGameError = (state: IGameReducer, payload: IFormError): IGameReducer => ({
	...state,
	errors: !isEmpty(payload?.place) ? omit(state.errors, [payload.place]) : {},
});

const onSetAnswers = (state: IGameReducer, payload: IAnswer[]): IGameReducer => ({
	...state,
	answers: payload,
});

const onUpdateQuestionAnswer = (state: IGameReducer, payload: IAnswer): IGameReducer => ({
	...state,
	answers: [
		...state.answers.filter(({questionId}) => questionId !== payload.questionId),
		payload,
	],
});

const onSaveUserAnswers = (state: IGameReducer): IGameReducer => ({
	...state,
	saveRequestState: RequestState.Requested,
});

const onSaveUserAnswersSuccess = (
	state: IGameReducer,
	payload: ISaveUserAnswersSuccess
): IGameReducer => ({
	...state,
	saveRequestState: RequestState.Received,
	step: ActiveStep.SUMMARY,
	savedOptions: payload.savedOptions,
});

const onSaveUserAnswersFailed = (state: IGameReducer, payload: IFormError): IGameReducer => ({
	...state,
	saveRequestState: RequestState.Received,
	errors: payload,
});

const onGetIsFriendlyCountrySuccess = (state: IGameReducer, payload: IsFriendly): IGameReducer => ({
	...state,
	isFriendlyCountry: payload,
});

const onChangeRequestState = (state: IGameReducer, payload: RequestState): IGameReducer => ({
	...state,
	saveRequestState: payload,
});

const onSetAnchorId = (state: IGameReducer, payload: null | number): IGameReducer => ({
	...state,
	anchorId: payload,
});

const onRequestUserAnswers = (state: IGameReducer): IGameReducer => ({
	...state,
	requestState: RequestState.Requested,
});

const onRequestUserAnswersSuccess = (
	state: IGameReducer,
	payload: IRequestUserAnswerSuccessResponse
): IGameReducer => ({
	...state,
	requestState: RequestState.Received,
	savedOptions: payload.savedOptions,
	answers: payload.answers,
	step: ActiveStep.SUMMARY,
});

const onRequestUserAnswersFailed = (state: IGameReducer): IGameReducer => ({
	...state,
	requestState: RequestState.Received,
});

export const game = createReducer<IGameReducer>({}, initialState)
	.on(setActiveStep, onSetActiveStep)
	.on(clearGameError, onClearGameError)
	.on(setAnswers, onSetAnswers)
	.on(updateQuestionAnswer, onUpdateQuestionAnswer)
	.on(editUserAnswers, onSaveUserAnswers)
	.on(saveUserAnswers, onSaveUserAnswers)
	.on(saveUserAnswersSuccess, onSaveUserAnswersSuccess)
	.on(saveUserAnswersFailed, onSaveUserAnswersFailed)
	.on(getIsFriendlyCountrySuccess, onGetIsFriendlyCountrySuccess)
	.on(changeRequestState, onChangeRequestState)
	.on(setAnchorId, onSetAnchorId)
	.on(requestUserAnswers, onRequestUserAnswers)
	.on(requestUserAnswersSuccess, onRequestUserAnswersSuccess)
	.on(requestUserAnswersFailed, onRequestUserAnswersFailed);
